import * as React from "react";
import { FormEvent, useEffect, useState } from "react";
import Typography from "@mui/material/Typography";
import { Theme } from "@mui/material/styles";
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import IdentificationForm from "../../../components/forms/survey/IdentificationForm";
import { useApp } from "../../../hooks/useApp";
import { StartSurvey, SurveyType, TSurveysState } from '../../../types/Survey';
import { useDispatch, useSelector } from "react-redux";
import { IAppState } from "../../../store/Store";
import { TApiUserDepartment, TApiUserLocation } from '../../../types/User';
import { TDispatch } from "../../../types/Thunk";
import { startSurvey } from "../../../actions/SurveyActions";
import { RouteComponentProps, withRouter } from "react-router";
import { reverse } from "named-urls";
import { routes } from "../../../Routes";
import { useCurrentActiveSurvey } from "../../../hooks/useCurrentActiveSurvey";
import SurveyTranslation from "../../../components/survey/SurveyTranslation";
import TextField from "@mui/material/TextField";
import { TAuthState } from "../../../types/Auth";
import { compose } from "redux";
import { withSurveyData } from "../../../components/hoc/requiresSurveyData";
import { setInspectionLocation } from "../../../actions/InspectionActions";
import { StaticContext } from "react-router";
import { determineSelectableDepLoc } from '../../../helpers/Survey';

type LocationState = {
  language: string;
  preview: boolean;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    heading: {
      fontSize: theme.typography.pxToRem(24),
      textAlign: "center"
    }
  })
);

const InspectionIdentificationPage: React.FC<RouteComponentProps<
  any,
  StaticContext,
  LocationState
>> = (props): JSX.Element => {
  const classes = useStyles({});
  const { app } = useApp();
  const dispatch = useDispatch<TDispatch>();
  const surveysState: TSurveysState = useSelector<IAppState, TSurveysState>(
    state => state.surveysState
  );
  const [error, setError] = useState(false);
  const { setRespondent, addToRespondentsStorage } = useCurrentActiveSurvey();
  const preview = props.location.state.preview;
  const auth: TAuthState = useSelector<IAppState, TAuthState>(
    (state: IAppState) => state.authState
  );

  const [selectableDepartments, setSelectableDepartments] = React.useState<Array<TApiUserDepartment>>([]);
  const [selectableLocations, setSelectableLocations] = React.useState<Array<TApiUserLocation>>([]);

  const [organization, setOrganization] = React.useState<TApiUserDepartment>({
    id: 0,
    title: "",
    children: []
  });

  const [location, setLocation] = React.useState<TApiUserLocation>({
    id: 0,
    title: "",
    children: []
  });

  const goToCategory = () => {
    props.history.push(
      reverse(routes.surveyCategory, {
        id: surveysState.currentSurvey?.survey.id as number
      })
    );
  };

  useEffect(() => {
    const currentSurvey = surveysState.currentSurvey;
    const currentSurveyIdentification = surveysState.currentSurveyIdentification;

    let selectableDepartments = currentSurveyIdentification?.departments ?? [];
    let selectableLocations = currentSurveyIdentification?.locations ?? [];

    if (currentSurvey?.survey.anonymity === SurveyType.REQUIRES_LOGIN) {
      const departments = auth.data?.user?.departments;
      const locations = auth.data?.user?.locations;

      selectableDepartments = determineSelectableDepLoc(selectableDepartments, departments);
      selectableLocations = determineSelectableDepLoc(selectableLocations, locations);
    }

    setSelectableDepartments(selectableDepartments);
    setSelectableLocations(selectableLocations);
  }, [auth.data, surveysState.currentSurvey, surveysState.currentSurveyIdentification]);

  useEffect(() => {
    const firstLocation = selectableLocations[0];
    if (firstLocation) {
      setLocation(firstLocation);
    }

    const firstDepartment = selectableDepartments[0];
    if (firstDepartment) {
      setOrganization(firstDepartment);
    }
  }, [selectableDepartments, selectableLocations]);

  const submitIdentificationForm = (
    e: FormEvent,
    anonymous: boolean = false
  ) => {
    e.preventDefault();

    const surveyData = {
      surveyId: surveysState.currentSurvey!.survey.id,
      languageCode: props.location.state.language,
      respondentName: auth.data?.user?.name,
      departmentId: organization?.id,
      locationId: location?.id,
      preview: preview,
    };

    dispatch(startSurvey(surveyData))
      .then((res) => {
        dispatch(setInspectionLocation(location?.id ?? 0));

        setRespondent(
          res.data.respondent,
          surveysState.currentSurvey!.survey.id,
          props.location.state.language,
          null,
          preview
        );
        if (surveysState.currentSurvey?.survey.allowSavingProgress) {
          addToRespondentsStorage(
            surveysState.currentSurvey?.survey.id,
            res.data.respondent
          );
        }
        goToCategory();
      })
      .catch(() => {
        setError(true);
      });
  };

  return (
    <>
      <Typography className={classes.heading}>
        {typeof surveysState.currentSurvey?.survey.segmentationHeader ===
        "string" ? (
          <>{surveysState.currentSurvey?.survey.segmentationHeader}</>
        ) : (
          <SurveyTranslation
            translate={"inspection.identification.before.begin.title"}
          />
        )}
      </Typography>
      <p>
        {typeof surveysState.currentSurvey?.survey.segmentationBody ===
        "string" ? (
          <>{surveysState.currentSurvey?.survey.segmentationBody}</>
        ) : (
          <SurveyTranslation
            translate={"inspection.identification.before.begin.description"}
          />
        )}
      </p>
      <TextField
        variant="outlined"
        disabled={true}
        margin="normal"
        fullWidth
        label={
          typeof surveysState.currentSurvey?.survey.segmentationId ===
          "string" ? (
            <>{surveysState.currentSurvey?.survey.segmentationId}</>
          ) : (
            <SurveyTranslation translate={"survey.identification.name"} />
          )
        }
        value={auth.data?.user?.name}
        type="text"
      />
      <IdentificationForm
        selectableLocations={selectableLocations}
        location={location}
        setLocation={setLocation}
        selectableDepartments={selectableDepartments}
        organization={organization}
        setOrganization={setOrganization}
        submitForm={submitIdentificationForm}
        survey={surveysState}
        errors={error && app.getError(StartSurvey)}
        employeeIdentification={false}
        segmentationContinueButton={surveysState.currentSurvey?.survey.segmentationContinueButton ?? null}
        segmentationId={surveysState.currentSurvey?.survey.segmentationId ?? null}
        selectSegmentationLocation={surveysState.currentSurvey?.survey.selectSegmentationLocation ?? null}
        selectSegmentationOrganisation={surveysState.currentSurvey?.survey.selectSegmentationOrganisation ?? null}
      />
    </>
  );
};

export default compose(
  withSurveyData,
  withRouter
)(InspectionIdentificationPage) as React.FC;
